import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import {
  Form,
  Formik
} from 'formik';
import storage from '../../../helpers/storage';
import {
  default as React,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useOutletContext, useNavigate } from 'react-router-dom';
import {
  useRecoilValue,
  useRecoilState,
  useSetRecoilState
} from 'recoil';
import { SubmitConfirmModal } from '../../../components/ConfirmModal/SubmitConfirmModal';
import { FormBuilder } from '../../../components/FormBuilder';
import {
  ErrorModalSection,
  FormSubmitHelper,
  SupportPopup,
  bankDetailsValidationSchema
} from '../../../components';
import { useLoader } from '../../../components/Loader/useLoader';
import {
  FormField
} from '../../../constants';
import { ApplicationRoutes, StaticRoutes } from '../../../Router';
import { StatusCodes } from 'http-status-codes';
import { formFields } from './FinancialDetailsConstant';
import { mapValues, stripEmpty } from '../../../helpers';
import {
  financialDetails,
  businessDetailsState,
  documentsListState,
  fundingDetailsState,
  applications
} from '../../../recoil';

import { useModal } from '../../../components/modalController/useModal';
import { findFirstValidationError } from '../../../helpers/formik';
import { useTealium } from '../../../tealium/useTealium';
import styles from './FinancialDetails.module.css';
import {
  setFinancialDetails,
  createFinancialDetails,
  submitBusinessApplication,
  checkApplicationExistsByBusinessIdNumber,
  getApplications
} from '../../../api/vodaLend';
import {
  useIsMobile
} from '../../../recoil';

export const FinancialDetails = () => {

  const businessId = localStorage.getItem('businessId');
  const [openModal, closeModal] = useModal();
  const listDocumentRecord = useRecoilValue(documentsListState);
  const [isOpenBusinessSubmissionWarning, setIsOpenBusinessSubmissionWarning] = useState(false);
  const [isOpenConfirmBox, setIsOpenConfirmBox] = useState(false);
  const [isDocumentUploaded, setIsDocumentUploaded] = useState(listDocumentRecord?.length > 0 ? true : false);
  const [details, setDetails] = useRecoilState(financialDetails);
  const businessDetails = useRecoilValue(businessDetailsState);
  const navigate = useNavigate();
  const setApplications = useSetRecoilState(applications);
  const { track, events } = useTealium();
  const location = useLocation();
  const [openLoader, closeLoader] = useLoader();
  const formRef = useRef();
  const accessTokenKey = 'loginState:accessToken';
  const [firstName, setFirstName] = useState('');
  let formTouched = false;
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const setFundingDetails = useSetRecoilState(fundingDetailsState);
  const isAppSubmitted = sessionStorage.getItem('isAppSubmitted');

  useEffect(() => {
    if (isAppSubmitted) {
      setIsOpenConfirmBox(true);
    }
  }, [isAppSubmitted]);

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.bankingDetails.formStart());
  };

  const isMobile = useRecoilValue(useIsMobile);

  useEffect(() => {
    track(events.bankingDetails.pageView());
  }, []);

  const formDetails = useMemo(() => ({
    ...details,
    accountHolderName: details?.accountHolderName || businessDetails?.name || '',
    businessRegistrationNumber: businessDetails?.regNumber || businessDetails?.vatNumber,
    bankName: details?.bankName || '',
    accountType: details?.accountType || '',
    accountNumber: details?.accountNumber?.toString() || ''
  }), [details]);

  const formatFormData = (formValues) => {
    const accountHolderName = formValues?.accountHolderName;
    const bankName = formValues?.bankName || '';
    const accountType = formValues?.accountType || '';
    const accountNumber = formValues?.accountNumber?.toString() || '';
    const formattedFormData = { accountHolderName, bankName, accountType, accountNumber };
    return mapValues(stripEmpty(formattedFormData));
  };

  const handleBack = () => {
    if (businessDetails?.businessType === 'Sole Proprietorship') {
      localStorage.setItem('loanApplicationStepKey', 'businessDetails');
      window.location.pathname = ApplicationRoutes.businessDetails;
    } else {
      window.location.pathname = ApplicationRoutes.businessDirectors;
    }
    backClick();
  };

  const CheckPostRequestType = (financialDetails) => {
    let checkPostRequestType;
    if (financialDetails.error) {
      checkPostRequestType = true;
    } else {
      checkPostRequestType = Object.values(financialDetails).every(x => x === undefined || x === null || x === '');
    }
    return checkPostRequestType;
  };

  const goToNextPage = async () => {
    openLoader();
    track(events.submitApplication.viewApplicationStatus());
    const allApps = await getApplications();
    setApplications(allApps);
    closeLoader();
    sessionStorage.removeItem('isAppSubmitted');
    sessionStorage.setItem('fromFinancial', 'true');
    navigate(`/applications/${businessId}`, { replace: false });
  };

  const goToProductCataloguePage = async() => {
    localStorage.setItem('loanApplicationStepKey', '');
    navigate(StaticRoutes.Products);
  };

  const handleSubmit = async (formValues) => {
    openLoader();
    const bankName = formValues?.bankName || '';
    track(events.bankingDetails.formEnd(bankName));

    if (formRef.current) {
      formRef.current.handleSubmit();
    }

    const accessTokenValue = await storage.getItem(accessTokenKey);
    setFirstName(accessTokenValue?.firstName);
    let financialDetailResponse = '';
    const formattedFormData = formatFormData(formValues);
    const isPostRequest = CheckPostRequestType(details);

    if (isPostRequest) {
      financialDetailResponse = await createFinancialDetails(formattedFormData);
    } else {
      financialDetailResponse = await setFinancialDetails(formattedFormData);
    }

    if ([StatusCodes.CREATED, StatusCodes.NO_CONTENT].includes(financialDetailResponse.status)) {
      const updatedFinancialDetails = isPostRequest ? financialDetailResponse.data : formattedFormData;
      setDetails(updatedFinancialDetails);
      const isApplicationExistsByBusinessIdNumber = await checkApplicationExistsByBusinessIdNumber(businessDetails?.regNumber || businessDetails?.vatNumber);
      if(isApplicationExistsByBusinessIdNumber.status === StatusCodes.OK) {
        if(isApplicationExistsByBusinessIdNumber.data.toString() === 'false') {
          const submitResponse = await submitBusinessApplication();
          if (submitResponse.status === StatusCodes.CREATED) {
            setIsOpenConfirmBox(true);
            track(events.submitApplication.pageView());
            setFundingDetails({});
            sessionStorage.setItem('isAppSubmitted', true);
          }
        } else {
          setIsOpenBusinessSubmissionWarning(true);
        }
      } else {
        openModal({
          title: 'Error',
          children: <ErrorModalSection
            traceId={isApplicationExistsByBusinessIdNumber?.data?.traceId}
            error={isApplicationExistsByBusinessIdNumber?.data?.error}
            onCloseErrorModal={closeErrorModal}
          />,
          type: 'error',
          onCloseModal: closeErrorModal
        });
      }
    }

    closeLoader();
  };

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  useEffect(() => {
    if (details.error) {

      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={details?.traceId}
          error={details?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
      return;
    }
    //#region commenting untill navigation to dashboard will not get complete (post submission journey)
    // if (submitState === submissionStates.submitting && !details.error) {
    //   setSubmitState(submissionStates.submitSuccess);
    // }
    //#endregion
  }, [details]);

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage));
  };

  const subHeadingText = !isDocumentUploaded ?
    'your funding application is under review. Please expect a response in the next 48 hours. Please remember to have your bank statements ready, as this will speed up the application process.' :
    'your funding application is under review. Please expect a response in the next 48 hours';

  const subHeading = () => {
    return (
      <>
        <span>Congratulations <span className={styles.userName} > {firstName}</span>, {subHeadingText}</span>
      </>
    );
  };

  const applicationFailSubHeading = () => {
    return(
      <>
        <span>This business has already applied for this product. Please select a different product from product catalogue.</span>
      </>
    );
  };

  const memoizedFormFields = useMemo(() => {
    return formFields(businessDetails?.businessType);
  }, [businessDetails?.businessType]);

  return (
    <div className={styles.container}>
      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          validationSchema={bankDetailsValidationSchema}
          validateOnMount={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);
            return (
              <>
                <Form className={styles.financialDetail} onFocus={handleFormFocus}>
                  <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />
                  <FormBuilder
                    fields={memoizedFormFields}
                    title="Bank details"
                    subtitle={'Enter your business bank information that we will use to verify your details and pay the funds into.'}
                    isMobile={isMobile}
                    shouldShowSubmit={true}
                    values={values}
                    errors={errors}
                    backClick={handleBack}
                    nextClick={nextClick}
                    touched={touched}
                    FormField={FormField}
                    submitState={submitState}
                    setIsDocumentUploaded={setIsDocumentUploaded}
                    setSubmitState={setSubmitState}
                    submissionStates={submissionStates}
                    shouldShowFileUpload={true}
                  />
                </Form>
                {isOpenConfirmBox &&
                  <SubmitConfirmModal
                    subHeading={subHeading()}
                    onButtonClick={goToNextPage}
                    heading='Business funding application success'
                    buttonText='View application status' />
                }

                {
                  isOpenBusinessSubmissionWarning &&
                  <SubmitConfirmModal
                    subHeading={applicationFailSubHeading()}
                    hideSuccessTick={true}
                    onButtonClick={goToProductCataloguePage}
                    buttonText='Got it!' />
                }
              </>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};
